import { InstructionType } from '@app/shared/enums/instruction-type.enum';
import { CancellationPolicyModel } from './cancellation-policy.model';
import { LocationModel } from './location.model';

export interface Price {
  currency: string;
  value: number;
}

export interface ServiceSummaryModel {
  _id: string;
  name: string;
  images?: string[];
}

export enum ServiceType {
  ECI_LCO = 'eci-lco',
  SLOTS = 'slots',
  RANGE = 'range',
  EVENT = 'event',
  VOUCHER = 'voucher',
}

export enum ServiceKind {
  LCO = 'late-check-out',
  ECI = 'early-check-in',
}

export enum ServiceBookingFlow {
  /** Default: quantity/options -> date -> time (resolved via /slots). */
  SELECTION_FIRST = 'selection-first',
  /** Inverted: date -> time -> available options (resolved via /available-options). */
  AVAILABILITY_FIRST = 'availability-first',
}

export enum ServicePricingType {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
  OPTIONS = 'options',
  /** Options come from an external provider (e.g. Blacklane) at booking time. */
  INTEGRATION = 'integration',
}

export interface ServiceModel {
  _id: string;
  name: string;
  price: Price;
  images?: string[];
  duration: number;
  interval?: number;
  description: string;
  categories: ServiceCategoryModel[];
  schedule: ScheduleDayModel[];
  serviceType: ServiceType;
  bookingFlow?: ServiceBookingFlow;
  providedAt: 'home' | 'city';
  cancellationPolicy: CancellationPolicyModel;
  faqs?: FaqsModel[];
  dates?: Dates;
  locations?: LocationModel[];
  minRequestsQuantity: number;
  maxRequestsQuantity: number;
  specialRequest?: SpecialRequest;
  requireConfirmation: {
    enabled: boolean;
    limit: number;
  };
  bookingLimits?: BookingLimitsModel;
  discount?: Discount[];
  score: number;
  tags: string[];
  byDay?: boolean;
  quantityLabel?: string;
  locationRequired: boolean;
  instructions?: ServiceInstructionModel;
  branch?: BranchModel;
  expiresInDays?: number;
  quota?: number;
  serviceKind: ServiceKind;
  addOns?: ServiceAddOnModel[];
  /**
   * Global limits on the total add-on quantity (summed across all selected
   * add-ons). `min: 0` keeps add-ons optional; `max: 0` means no cap.
   */
  addOnsLimits?: {
    min: number;
    max: number;
  };
  pricingOptions?: ServicePricingOptionModel[];
  includedServices?: ServiceSummaryModel[];
  expireInDays?: number;
  additionalInformation?: Record<string, any>;
  pricingType?: ServicePricingType;
  /**
   * SurveyJS form shown below the date fields for integration-priced services.
   * Its answers travel as `context` to /available-options. Set via DB (Phase 1).
   */
  preFetchDynamicFields?: Record<string, any>;
}

/** How the minimum lead time of `bookingLimits` is interpreted. */
export enum BookingLimitMinTimeMode {
  /** `minTime` hours before the slot. */
  RELATIVE = 'relative',
  /** Cutoff at `minTimeFixed.time`, `daysPrior` days before the slot's day. */
  FIXED = 'fixed',
}

export interface BookingLimitsMinTimeFixedModel {
  /** "HH:mm" in the service (branch) timezone. */
  time: string;
  /** 0 = the slot's own day, 1 = the day before, etc. */
  daysPrior: number;
}

/**
 * Booking window configured for the service. `minTime`/`maxTime` are hours and
 * 0 means unlimited on that end. `minTimeMode` defaults to `relative` for
 * services created before the fixed cutoff existed.
 */
export interface BookingLimitsModel {
  minTime: number;
  maxTime: number;
  minTimeMode?: BookingLimitMinTimeMode;
  minTimeFixed?: BookingLimitsMinTimeFixedModel;
}

export interface ServicePercentagePriceModel {
  percentage: number;
  commission: Price;
}

export interface ServiceDiscountByQuantityModel {
  quantity: number;
  percentage: number;
}

export interface ServicePricingOptionModel {
  _id: string;
  name: string;
  description?: string;
  price: Price;
  externalId?: string;
  images?: string[];
}

export interface ServiceAddOnModel {
  _id: string;
  name: string;
  price: Price;
  basePrice: Price;
  enabled: boolean;
  description: string;
  image: string;
  /** Max units the guest can pick (e.g. 1 for Tripwip insurances). */
  maxQuantity?: number;
  /** Provider's own reference (e.g. Tripwip insurance name). */
  externalId?: string;
}

export interface ServicePricingOption {
  _id: string;

  name: string;

  price: Price;

  basePrice: Price;

  description?: string;

  images?: string[];
}

export interface SelectedAddOnModel extends ServiceAddOnModel {
  quantity: number;
}

export interface ProviderBrandingModel {
  logo: string;
}

export interface ProviderModel {
  _id: string;
  name: string;
  branding: ProviderBrandingModel;
  metadata: {
    createdAt: string;
    updatedAt: string;
  };
}

export interface BranchModel {
  _id: string;
  name: string;
  currency: string;
  provider: ProviderModel;
  location: LocationModel;
  googlePlaceId?: string;
  coverageArea: {
    coordinates: number[][][];
  };
  metadata: {
    createdAt: string;
    updatedAt: string;
  };
  usersNotification: string[];
}

export interface ServiceInstructionModel {
  type: InstructionType;
  steps: ServiceInstructionStepModel[];
}

export interface ServiceInstructionStepModel {
  index?: number;
  description: string;
  image?: string;
}

export interface SpecialRequest {
  mandatory: boolean;
  placeholder: string;
}

export interface Discount {
  quantity: number;
  percentage: number;
}

export interface Dates {
  from: string;
  to: string;
}

export interface ServiceCategoryModel {
  _id: string;
  key: string;
  label: string;
  iconUrl: string;
  coverUrl: string;
  sort: number;
}

export interface ScheduleDayModel {
  day: string;
  enabled: boolean;
  ranges: ScheduleRangeModel[];
}

export interface ScheduleRangeModel {
  fromHour: number;
  fromMinute: number;
  toHour: number;
  toMinute: number;
}
export interface FaqsModel {
  question: string;
  answer: string;
}
